'use client';

import * as React from 'react';
import useRoutePath from '@/hooks/useRoutePath';
import { Links } from '@/lib/types/customer/restaurant/RestaurantConfig';
import { useTranslations } from 'next-intl';
import type { ActiveSubscription } from '@/lib/types/customer/restaurant/ActiveSubscription';
import Link from 'next/link';
import If from '@ui/If';
import { cn } from '@/lib/utils';
import Image, { type StaticImageData } from 'next/image';
import type { Store } from '@/lib/types/customer/restaurant/StoreProfile';
import * as pixel from '@/lib/utils/fpixel';
import {
  getClickIdForEvent,
  markEventAsTracked,
} from '@/lib/utils/adcashTracking';

// import menu from '/customer/icons/menu.png';
// import menuAndOrder from '@/../public/customer/icons/menu-and-order.png';
// import tableReservation from '@/../public/customer/icons/table-reservation.png';
// import onlineShop from '@/../public/customer/icons/online-shop.png';
// import bed from '@/../public/customer/icons/bed.png';

import menu from '@/assets/icons/menu.png';
import menuAndOrder from '@/assets/icons/menu-and-order.png';
import tableReservation from '@/assets/icons/table-reservation.png';
import onlineShop from '@/assets/icons/online-shop.png';
import bed from '@/assets/icons/bed.png';

import { SHOP_TYPES } from '@/constant/configs';
import { useUserStore } from '@/stores/customer/user';

type Props = {
  store: Store;
  links: Links | null;
  activeSubscription: ActiveSubscription;
  isDisabled: boolean;
};

const VendorList = ({ links, store, isDisabled }: Props) => {
  const t = useTranslations('storefront.store_profile');
  const { pathname } = useRoutePath();
  const user = useUserStore((state) => state.user);
  const tableReservationLink = links?.table_reservation || null;
  const roomReservationLink = links?.room_reservation || null;
  const onlineShopLink = links?.online_shop || null;
  const menuFile = store?.media?.menu_file?.[0]?.url || null;
  const showFoodMenuButton =
    store?.features?.crud_on_menus && store.type !== SHOP_TYPES.online_shop;
  const orderEnabled = store?.features?.handle_orders;
  // const handleLinks = store?.features?.ui_backoffice_storefront_handle_links;

  const handleOnlineShopClick = (link: string) => {
    if (isDisabled) return;
    // Existing Facebook Pixel tracking
    pixel.customEvent('OnlineShopClick', { shop: store.slug });
    // pixel.trackCustomToPixel(pixel.B2C_FB_PIXEL_ID, 'OnlineShopClick', {
    //   shop: store.slug,
    // });

    // Adcash tracking (fire and forget) - only track if not already tracked
    const adcashClickId = getClickIdForEvent('button_click');
    if (adcashClickId) {
      const payload = JSON.stringify({
        clickId: adcashClickId,
        buttonContext: 'online_shop',
        variable: 'OnlineShopButton',
        metadata: { shop: store.slug },
      });

      // Use sendBeacon for non-blocking tracking before navigation
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        const sent = navigator.sendBeacon('/api/adcash/button-click', blob);
        if (sent) {
          markEventAsTracked('button_click');
        }
      } else {
        // Fallback to fetch with keepalive
        fetch('/api/adcash/button-click', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        })
          .then(() => markEventAsTracked('button_click'))
          .catch((err) =>
            console.error('[Adcash] Button tracking failed:', err)
          );
      }
    }

    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="">
      <div className="py-16">
        <div className="grid grid-cols-1 gap-y-8 sm:grid-cols-2 sm:gap-4 sm:gap-y-10 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
          <If condition={onlineShopLink}>
            <OnlineShopButton
              icon={onlineShop}
              label={t('online_shop_button')}
              disabled={isDisabled}
              handleClick={() =>
                handleOnlineShopClick(onlineShopLink as string)
              }
            />
          </If>
          <If condition={showFoodMenuButton}>
            <VendorLinkButtons
              href={`${pathname}/menus`}
              icon={menu}
              label={orderEnabled ? t('menu_order') : t('menu')}
            />
          </If>
          {!showFoodMenuButton && menuFile && (
            <VendorLinkButtons
              href={menuFile}
              icon={menuAndOrder}
              label={t('menu')}
              newTab={true}
            />
          )}

          <If condition={tableReservationLink}>
            <VendorLinkButtons
              href={tableReservationLink || ''}
              icon={tableReservation}
              label={t('table_reserve_button')}
              newTab={true}
            />
          </If>

          <If condition={roomReservationLink}>
            <VendorLinkButtons
              href={roomReservationLink || ''}
              icon={bed}
              label={t('room_reserve_button')}
              newTab={true}
            />
          </If>
        </div>
      </div>

      <VendorExternalLinks links={links} />
    </section>
  );
};

export default VendorList;

function VendorLinkButtons({
  href,
  icon,
  label,
  disabled,
  newTab = false,
  handleClick,
}: {
  href: string;
  icon: StaticImageData;
  label: string;
  disabled?: boolean;
  newTab?: boolean;
  handleClick?: () => void;
}) {
  return (
    <div className="relative">
      <Link
        href={href}
        target={newTab ? '_blank' : '_self'}
        className={cn(
          'group relative flex min-h-[41px] w-full items-center justify-between overflow-hidden rounded-xl bg-white text-center font-medium leading-5 text-title shadow-lg transition-all duration-300 ease-in-out hover:scale-105 hover:text-primary hover:shadow-red-500/30 focus:outline-none sm:whitespace-pre-wrap',
          disabled && 'pointer-events-none opacity-60'
        )}
        onClick={handleClick}
      >
        <span className="relative z-10 py-2 pl-4 text-left xl:pl-2">
          {label}
        </span>
        <div className="relative flex h-full items-center justify-center py-3 pl-5 pr-2">
          <div className="relative z-10 h-8 w-8">
            <Image
              src={icon}
              fill
              alt={label + ' icon'}
              className="object-contain"
            />
          </div>
          <div
            className="absolute inset-0 h-full w-full bg-[#333333] transition-colors duration-300 group-hover:bg-primary"
            style={{
              clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0% 100%)',
            }}
          ></div>
        </div>
      </Link>
      {/* {label === t('online_shop_button') && (
        <span className="absolute ml-1 translate-y-1 text-left text-xs text-primary">
          {t('online_shop_hint')}
        </span>
      )} */}
    </div>
  );
}

function OnlineShopButton({
  icon,
  label,
  disabled,
  handleClick,
}: {
  icon: StaticImageData;
  label: string;
  disabled?: boolean;
  handleClick?: () => void;
}) {
  return (
    <div className="relative">
      <button
        className={cn(
          'group relative flex min-h-[41px] w-full items-center justify-between overflow-hidden rounded-xl bg-white text-center font-medium leading-5 text-title shadow-lg transition-all duration-300 ease-in-out hover:scale-105 hover:text-primary hover:shadow-red-500/30 focus:outline-none sm:whitespace-pre-wrap',
          disabled && 'pointer-events-none opacity-60'
        )}
        onClick={handleClick}
      >
        <span className="relative z-10 py-2 pl-4 text-left xl:pl-2">
          {label}
        </span>
        <div className="relative flex h-full items-center justify-center py-3 pl-5 pr-2">
          <div className="relative z-10 h-8 w-8">
            <Image
              src={icon}
              fill
              alt={label + ' icon'}
              className="object-contain"
            />
          </div>
          <div
            className="absolute inset-0 h-full w-full bg-[#333333] transition-colors duration-300 group-hover:bg-primary"
            style={{
              clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0% 100%)',
            }}
          ></div>
        </div>
      </button>
      {/* {label === t('online_shop_button') && (
        <span className="absolute ml-1 translate-y-1 text-left text-xs text-primary">
          {t('online_shop_hint')}
        </span>
      )} */}
    </div>
  );
}

function VendorExternalLinks({ links }: { links: Links | null }) {
  const lieferando = links?.lieferando || '';
  const uberEats = links?.uber_eats || '';
  const wolt = links?.wolt || '';

  const buttons = [
    {
      text: 'Lieferando',
      href: lieferando,
      logo: '/store/vendor-logos/lieferando.png',
    },
    {
      text: 'Uber Eats',
      href: uberEats,
      logo: '/store/vendor-logos/uber-eats-logo.png',
    },
    {
      text: 'Wolt',
      href: wolt,
      logo: '/store/vendor-logos/wolt.png',
    },
  ].filter((button) => button.href);

  if (buttons.length === 0) return null;

  return (
    <div className="pb-12">
      {/* Improved responsive grid layout with no background styling */}
      <div className="xs:grid-cols-2 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
        {buttons.map((button, index) => (
          <Link
            key={index}
            href={button.href}
            target="_blank"
            className="group/button hover:shadow-xs relative inline-flex items-center overflow-hidden transition-all duration-300 ease-in-out hover:scale-105"
          >
            <Image
              src={button.logo}
              width={index === 2 ? 120 : 200}
              height={index === 2 ? 40 : 50}
              alt={button.text + ' logo'}
              className="object-contain"
            />
            <div className="absolute inset-0 flex h-full w-full justify-center [transform:skew(-13deg)_translateX(-100%)] group-hover/button:duration-1000 group-hover/button:[transform:skew(-13deg)_translateX(100%)]">
              <div className="relative h-full w-8 bg-white/20" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
